import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import MobileBottomNav from './components/MobileBottomNav';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import RegisterInvite from './pages/RegisterInvite';
import RegisterGuest from './pages/RegisterGuest';
import Home from './pages/Home';
import Profile from './pages/Profile';
import Members from './pages/Members';
import Board from './pages/Board';
import Messages from './pages/Messages';
import Notifications from './pages/Notifications';
import Admin from './pages/Admin';
import CompanyProfile from './pages/CompanyProfile';
import { useState, useEffect } from 'react';

import { ThemeProvider } from './context/ThemeContext';
import { NotificationProvider } from './context/NotificationContext';
import { LanguageProvider } from './context/LanguageContext';

import ErrorBoundary from './components/ErrorBoundary';

function App() {

  const [user, setUser] = useState(() => {
    try {
      const savedUser = localStorage.getItem('user');
      return savedUser ? JSON.parse(savedUser) : null;
    } catch (err) {
      console.error('Failed to parse user from localStorage', err);
      return null;
    }
  });

  const [appError, setAppError] = useState(null);

  const handleLogin = (userData) => {
    console.log('Login logic initiated with data:', userData);
    if (!userData || typeof userData !== 'object' || (!userData.user && !userData.token)) {
      setAppError('Invalid authentication response from network.');
      return;
    }

    // Check if user is an error object
    if (userData.user && typeof userData.user.username === 'object') {
      setAppError('Core identity corruption detected. Trace: ' + JSON.stringify(userData.user));
      return;
    }

    localStorage.setItem('user', JSON.stringify(userData.user));
    localStorage.setItem('token', userData.token);
    setUser(userData.user);
    setAppError(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    // Clear the analytics session cache
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('viewed_profile_') || key.startsWith('viewed_post_')) {
        sessionStorage.removeItem(key);
      }
    });
    setUser(null);
    setAppError(null);
  };

  // Role-based route access helpers
  const canAccessBoard = user && user.role !== 'guest';
  const canAccessMessages = user && user.role !== 'guest';
  const isAdmin = user?.role === 'ap_admin';

  return (
    <ErrorBoundary>
      <LanguageProvider>
        <ThemeProvider>
          <NotificationProvider user={user}>
            <Router>
              <div className="min-h-screen">
                {user && <Navbar user={user} onLogout={handleLogout} />}
                <main className="container page-container">
                  {appError && (
                    <div style={{
                      background: 'rgba(244, 114, 182, 0.1)', border: '1px solid rgba(244, 114, 182, 0.2)',
                      color: 'var(--accent-magenta)', padding: '1rem', borderRadius: '12px',
                      marginBottom: '1.5rem', textAlign: 'center', fontSize: '0.85rem', fontWeight: 600
                    }}>
                      {typeof appError === 'object' ? (appError.message || JSON.stringify(appError)) : appError}
                    </div>
                  )}
                  <Routes>
                    {/* Public / Auth routes */}
                    <Route path="/login" element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />} />
                    <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/" />} />
                    <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to="/" />} />
                    <Route path="/register-invite" element={!user ? <RegisterInvite /> : <Navigate to="/" />} />
                    <Route path="/register-guest" element={!user ? <RegisterGuest /> : <Navigate to="/" />} />

                    {/* Authenticated routes */}
                    <Route path="/" element={user ? <Home /> : <Navigate to="/login" />} />
                    <Route path="/profile/:id" element={user ? <Profile /> : <Navigate to="/login" />} />
                    <Route path="/members" element={user ? <Members /> : <Navigate to="/login" />} />
                    <Route path="/opportunities" element={canAccessBoard ? <Board /> : <Navigate to={user ? "/" : "/login"} />} />
                    <Route path="/messages" element={canAccessMessages ? <Messages /> : <Navigate to={user ? "/" : "/login"} />} />
                    <Route path="/notifications" element={user ? <Notifications /> : <Navigate to="/login" />} />
                    <Route path="/company/:id" element={user ? <CompanyProfile /> : <Navigate to="/login" />} />
                    <Route path="/admin" element={isAdmin ? <Admin /> : <Navigate to="/" />} />
                  </Routes>
                </main>
                {user && <MobileBottomNav user={user} />}
              </div>
            </Router>
          </NotificationProvider>
        </ThemeProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );

}

export default App;
